/**
 * Carrega etapas da org e controla o picker funil/etapa do destino.
 */
import { useEffect, useMemo, useState } from 'react';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { useLeads } from '../../../../contexts/leads/LeadsContext';
import { supabase } from '../../../../lib/supabase';
import type { FunnelStage } from '../../../../types';

export function useFollowUpDestinationPicker(stageIds: string[], onAddStage: (stageId: string) => void) {
    const { currentOrganization } = useOrganization();
    const { funnels, selectedFunnelId } = useLeads();
    const [allStages, setAllStages] = useState<FunnelStage[]>([]);
    const [pickerFunnelId, setPickerFunnelId] = useState('');
    const [pickerStageId, setPickerStageId] = useState('');

    useEffect(() => {
        const organizationId = currentOrganization?.id;
        if (!organizationId) {
            setAllStages([]);
            return;
        }

        let cancelled = false;
        void supabase
            .from('funnel_stages')
            .select('id, name, color, funnel_id, "order"')
            .eq('organization_id', organizationId)
            .order('order', { ascending: true })
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error) {
                    console.error('[FollowUpDestinationFields] etapas:', error);
                    setAllStages([]);
                    return;
                }
                setAllStages(
                    (data ?? []).map((row) => ({
                        id: row.id,
                        name: row.name,
                        conversionEvent: '',
                        color: row.color,
                        funnelId: row.funnel_id ?? undefined,
                        order: row.order ?? undefined,
                    })),
                );
            });

        return () => { cancelled = true; };
    }, [currentOrganization?.id]);

    const defaultFunnelId = selectedFunnelId ?? funnels[0]?.id ?? '';

    useEffect(() => {
        if (!defaultFunnelId) return;
        setPickerFunnelId((current) => current || defaultFunnelId);
    }, [defaultFunnelId]);

    const pickerStages = useMemo(
        () => allStages.filter((stage) => !pickerFunnelId || !stage.funnelId || stage.funnelId === pickerFunnelId),
        [allStages, pickerFunnelId],
    );

    const selectedRows = useMemo(
        () => stageIds.map((id) => {
            const stage = allStages.find((item) => item.id === id);
            const funnel = funnels.find((item) => item.id === stage?.funnelId);
            return {
                id,
                stageName: stage?.name ?? 'Etapa',
                funnelName: funnel?.name ?? 'Funil',
                color: stage?.color,
            };
        }),
        [allStages, funnels, stageIds],
    );

    const handleFunnelChange = (funnelId: string) => {
        setPickerFunnelId(funnelId);
        setPickerStageId('');
    };

    const handleStageChange = (stageId: string) => {
        setPickerStageId(stageId);
        if (!stageId) return;
        onAddStage(stageId);
        setPickerStageId('');
    };

    return {
        funnels,
        pickerFunnelId,
        pickerStageId,
        pickerStages,
        selectedRows,
        handleFunnelChange,
        handleStageChange,
    };
}
